import { motion } from 'framer-motion';
import { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';

const WithdrawalHistory = ({ theme = 'dark' }) => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null); // store as 'YYYY-MM-DD' or null
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastId, setLastId] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();
  const containerRef = useRef(null);
  const isInitialMount = useRef(true);
  const isFetching = useRef(false);

  const isDark = theme === 'dark';

  // Fetch withdrawals function
  const fetchWithdrawals = async ({
    last_id = 0,
    isLoadingMore = false,
    date = '',
    status = 'all',
  }) => {
    if (isFetching.current) return;

    try {
      isFetching.current = true;
      setLoading(true);
      setError(null);

      const formData = {
        token: localStorage.getItem('a5srf4wer7w1cd'),
        last_id,
        date,
        status,
      };

      const response = await axios.post(`${BASE_API_URL}/payment/withdrawl-report`, formData);
      const data = response?.data;

      if (data?.error) {
        if (data?.error === 'session expired') {
          localStorage.removeItem('a5srf4wer7w1cd');
          navigate('/login');
          return;
        } else {
          toast.error(data?.error);
          return;
        }
      }

      if (data?.data) {
        setHasMore(data?.has_more);
        setLastId(data?.last_id);

        if (isLoadingMore) {
          setWithdrawals(prevData => [...prevData, ...data.data]);
        } else {
          setWithdrawals(data.data);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to fetch withdrawals');
      setError('Failed to fetch withdrawal history. Please try again.');
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  // Fetch initial data
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchWithdrawals({
        last_id: 0,
        isLoadingMore: false,
        status: 'all',
        date: ''
      });
    }
  }, []);

  // Refetch when status changes
  useEffect(() => {
    if (!isInitialMount.current) {
      setWithdrawals([]);
      setLastId(0);
      setHasMore(true);
      fetchWithdrawals({
        last_id: 0,
        date: selectedDate || '',
        status: selectedStatus,
        isLoadingMore: false
      });
    }
  }, [selectedStatus]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!containerRef.current || loading || !hasMore || withdrawals.length === 0) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (isNearBottom) {
      fetchWithdrawals({
        last_id: lastId,
        status: selectedStatus,
        isLoadingMore: true,
        date: selectedDate || ''
      });
    }
  }, [hasMore, loading, lastId, withdrawals.length, selectedStatus, selectedDate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout;
    const throttledScrollHandler = () => {
      if (scrollTimeout) return;

      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    };

    container.addEventListener('scroll', throttledScrollHandler);

    return () => {
      container.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);

  const statusStyles = {
    "0": { class: 'status-pending', text: 'Pending' },
    "1": { class: 'status-completed', text: 'Completed' },
    "2": { class: 'status-rejected', text: 'Rejected' },
  };

  const filteredWithdrawals = withdrawals.filter(item =>
    selectedStatus === 'all' || item.status === selectedStatus
  );

  const getStatusInfo = (status) => {
    return statusStyles[status] || { class: '', text: 'Unknown' };
  };

  const formatDate = (dateString) => {
    return moment(dateString, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YY hh:mm A');
  };

  const formatAmount = (amount) => {
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return '$0.00';
    return `$${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleShowDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedWithdrawal(null);
  };

  const handleResetDate = () => {
    setSelectedDate(null);
    fetchWithdrawals({
      last_id: 0,
      date: '',
      status: selectedStatus,
      isLoadingMore: false
    });
  };

  const handleRetry = () => {
    fetchWithdrawals({
      last_id: 0,
      isLoadingMore: false,
      date: selectedDate || '',
      status: selectedStatus
    });
  };

  const formatWalletType = (walletType) => {
    if (!walletType) return 'Unknown';

    const formatted = walletType
      .replace('usdt-', 'USDT ')
      .replace('bep20', 'BEP20')
      .replace('erc20', 'ERC20')
      .replace('trc20', 'TRC20')
      .toUpperCase();

    return formatted;
  };

  const mutedTextColor = isDark ? 'var(--wh-muted)' : '#6c757d';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mobile-container"
      ref={containerRef}
      style={{
        '--wh-bg': isDark ? '#020617' : '#f8f9fa',
        '--wh-text-main': isDark ? '#e5e7eb' : '#111827',
        '--wh-card-bg': isDark ? '#020617' : '#ffffff',
        '--wh-card-border': isDark ? '#1f2937' : '#dee2e6',
        '--wh-muted': isDark ? '#9ca3af' : '#6c757d',
        '--wh-header-title': isDark ? '#f9fafb' : '#333333',
        '--wh-icon': isDark ? '#60a5fa' : '#007bff',
        '--wh-detail-section-bg': isDark ? '#020617' : '#f8f9fa',
        '--wh-detail-section-border': isDark ? '#1f2937' : '#e9ecef',
        '--wh-modal-bg': isDark ? '#020617' : '#ffffff',
        '--wh-modal-border': isDark ? '#1f2937' : '#dee2e6',
      }}
    >
      <div className="header">
        <div className="withdrawal-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 8.5H14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6 16.5H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.5 16.5H14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 12H18L16 15H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 12H6L8 9H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 8.5H17.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 15.5H6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 15.5H14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 9L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 15L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h2 className="page-title">Withdrawal History</h2>
      </div>

      <div className="filters-section">
        <div className="row g-3">
          <div className="col-6">
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={loading}
            >
              <option value="all">All</option>
              <option value="0">Pending</option>
              <option value="1">Completed</option>
              <option value="2">Rejected</option>
            </select>
          </div>
          <div className="col-6">
            <div className="datepicker-wrapper">
              <DatePicker
                selected={selectedDate ? new Date(selectedDate) : null}
                onChange={(date) => {
                  if (!date) {
                    handleResetDate();
                    return;
                  }
                  const formatted = new Date(date).toISOString().slice(0, 10);
                  setSelectedDate(formatted);

                  fetchWithdrawals({
                    last_id: 0,
                    date: formatted,
                    status: selectedStatus,
                    isLoadingMore: false
                  });
                }}
                className="form-control datepicker-input"
                placeholderText="DD/MM/YYYY"
                dateFormat="dd/MM/yyyy"
                value={selectedDate != null ? moment(selectedDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}
                disabled={loading}
                withPortal
                adjustDateOnChange
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
              {selectedDate && (
                <button
                  className="date-reset-btn"
                  onClick={handleResetDate}
                  type="button"
                  title="Clear date"
                  disabled={loading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="error-state"
        >
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="error-message">{error}</p>
          <button className="btn-retry" onClick={handleRetry}>
            Try Again
          </button>
        </motion.div>
      )}

      {/* Withdrawals List */}
      <div className="withdrawals-list">
        {!error && filteredWithdrawals.length === 0 && !loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="empty-state"
          >
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 10H21M7 15H8M12 15H13M6 6H18C19.1046 6 20 6.89543 20 8V16C20 17.1046 19.1046 18 18 18H6C4.89543 18 4 17.1046 4 16V8C4 6.89543 4.89543 6 6 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="empty-message">No withdrawals found</p>
            <p className="empty-submessage">Try changing your filters or check back later</p>
          </motion.div>
        ) : (
          <>
            {filteredWithdrawals.map((withdrawal) => {
              const statusInfo = getStatusInfo(withdrawal.status);
              return (
                <motion.div
                  key={withdrawal.withdrawl_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="withdrawal-card"
                >
                  <div className="row align-items-center">
                    <div className="col-8">
                      <div className="withdrawal-id">ID: {withdrawal.id}</div>
                      <div className="withdrawal-date">{formatDate(withdrawal.date)}</div>
                      <div className="withdrawal-type">
                        {withdrawal.type === 'bank' ? 'Bank Transfer' : 'Crypto'}
                        {withdrawal.type === 'crypto' && withdrawal.wallet_type && (
                          <small className="wallet-type-badge">
                            {formatWalletType(withdrawal.wallet_type)}
                          </small>
                        )}
                      </div>
                      {withdrawal.charge > 0 && (
                        <div className="charge-info">
                          Charge: {formatAmount(withdrawal.charge)} | Net: {formatAmount(withdrawal.net)}
                        </div>
                      )}
                    </div>
                    <div className="col-4 text-end">
                      <div className="withdrawal-amount">{formatAmount(withdrawal.amount)}</div>
                      <div className={`status-badge ${statusInfo.class}`}>
                        {statusInfo.text}
                      </div>
                      <button
                        className="btn-details"
                        onClick={() => handleShowDetails(withdrawal)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                          <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Details
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="loading-indicator"
              >
                <div className="spinner"></div>
                <p>
                  {withdrawals.length === 0 ? 'Loading withdrawal history...' : 'Loading more withdrawals...'}
                </p>
              </motion.div>
            )}

            {!hasMore && filteredWithdrawals.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="end-of-list"
              >
                <p>No more withdrawals to load</p>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedWithdrawal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="modal-backdrop"
          onClick={handleCloseModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title">Withdrawal Details</h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-item">
                <span className="detail-label">Withdrawal ID:</span>
                <span className="detail-value">{selectedWithdrawal.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Transaction ID:</span>
                <span className="detail-value transaction-id">{selectedWithdrawal.withdrawl_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Date & Time:</span>
                <span className="detail-value">{formatDate(selectedWithdrawal.date)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">{formatAmount(selectedWithdrawal.amount)}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Processing Fee:</span>
                <span className="detail-value text-danger">-{formatAmount(selectedWithdrawal.charge)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Net Amount:</span>
                <span className="detail-value text-success">{formatAmount(selectedWithdrawal.net)}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Type:</span>
                <span className="detail-value text-capitalize">{selectedWithdrawal.type}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`status-badge-modal ${getStatusInfo(selectedWithdrawal.status).class}`}>
                  {getStatusInfo(selectedWithdrawal.status).text}
                </span>
              </div>

              {selectedWithdrawal.type === 'bank' && (
                <div className="detail-section">
                  <h6>Bank Details</h6>
                  <div className="detail-item">
                    <span className="detail-label">Bank Name:</span>
                    <span className="detail-value">{selectedWithdrawal.bank_name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Account Number:</span>
                    <span className="detail-value">{selectedWithdrawal.account_no || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">IFSC Code:</span>
                    <span className="detail-value">{selectedWithdrawal.ifsc_code || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Account Holder:</span>
                    <span className="detail-value">{selectedWithdrawal.holder || 'N/A'}</span>
                  </div>
                </div>
              )}

              {selectedWithdrawal.type === 'crypto' && (
                <div className="detail-section">
                  <h6>Crypto Details</h6>
                  <div className="detail-item">
                    <span className="detail-label">Network:</span>
                    <span className="detail-value text-uppercase">{formatWalletType(selectedWithdrawal.wallet_type)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Wallet Address:</span>
                    <span className="detail-value wallet-address">{selectedWithdrawal.wallet_address}</span>
                  </div>
                </div>
              )}

              {selectedWithdrawal.status === "2" && selectedWithdrawal.reject_reason && (
                <div className="detail-section reject-section">
                  <h6>Reject Reason</h6>
                  <div className="detail-item">
                    <span className="detail-value reject-reason text-danger">{selectedWithdrawal.reject_reason}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <style>{`
        .mobile-container {
          max-width: 400px;
          margin: 0 auto;
          padding: 20px;
          background-color: var(--wh-bg);
          min-height: 100vh;
          overflow-y: auto;
          height: 100vh;
          color: var(--wh-text-main);
        }

        .header {
          text-align: center;
          margin-bottom: 30px;
        }

        .withdrawal-icon {
          margin-bottom: 15px;
          color: var(--wh-icon);
        }

        .page-title {
          color: var(--wh-header-title);
          font-weight: 600;
          margin: 0;
          font-size: 1.5rem;
        }

        .filters-section {
          margin-bottom: 25px;
        }

        .form-select, .form-control {
          border-radius: 8px;
          border: 1px solid var(--wh-card-border);
          padding: 10px 15px;
          font-size: 14px;
          background-color: var(--wh-card-bg);
          color: var(--wh-text-main);
        }

        .form-select:focus, .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .form-select:disabled, .form-control:disabled {
          background-color: #1f2933;
          opacity: 0.6;
          cursor: not-allowed;
        }

        .datepicker-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .datepicker-input {
          padding-right: 40px;
          width: 100%;
        }

        .date-reset-btn {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: ${mutedTextColor};
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .date-reset-btn:hover:not(:disabled) {
          background-color: rgba(148, 163, 184, 0.2);
          color: #dc3545;
        }

        .date-reset-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .withdrawal-card {
          background: var(--wh-card-bg);
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 12px;
          border: 1px solid var(--wh-card-border);
        }

        .withdrawal-id {
          font-weight: 600;
          color: var(--wh-text-main);
          margin-bottom: 5px;
          font-size: 14px;
        }

        .withdrawal-date {
          color: var(--wh-muted);
          font-size: 12px;
          margin-bottom: 5px;
        }

        .withdrawal-type {
          color: var(--wh-text-main);
          font-size: 13px;
          margin-bottom: 5px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .wallet-type-badge {
          background: rgba(148, 163, 184, 0.2);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          color: var(--wh-text-main);
        }

        .charge-info {
          color: var(--wh-muted);
          font-size: 11px;
          margin-top: 4px;
        }

        .withdrawal-amount {
          font-weight: 700;
          color: var(--wh-header-title);
          margin-bottom: 8px;
          font-size: 16px;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          margin-bottom: 8px;
          display: inline-block;
        }

        .status-badge-modal {
          padding: 6px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          display: inline-block;
        }

        .status-pending {
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        }

        .status-completed {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .status-rejected {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .btn-details {
          background: #6c757d;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: background 0.3s;
          display: inline-flex;
          align-items: center;
        }

        .btn-details:hover {
          background: #545b62;
        }

        .loading-indicator {
          text-align: center;
          padding: 20px;
        }

        .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid #4b5563;
          border-top: 3px solid #0d6efd;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 10px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-indicator p {
          color: var(--wh-muted);
          font-size: 14px;
          margin: 0;
        }

        .end-of-list {
          text-align: center;
          padding: 20px;
          color: var(--wh-muted);
          font-size: 14px;
          border-top: 1px solid var(--wh-card-border);
          margin-top: 10px;
        }

        .error-state {
          text-align: center;
          padding: 40px 20px;
        }

        .error-icon {
          color: #dc3545;
          margin-bottom: 20px;
        }

        .error-message {
          color: #fca5a5;
          margin-bottom: 20px;
          font-size: 16px;
        }

        .btn-retry {
          background: #0d6efd;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.3s;
        }

        .btn-retry:hover {
          background: #0b5ed7;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }

        .empty-icon {
          color: var(--wh-muted);
          margin-bottom: 20px;
          opacity: 0.5;
        }

        .empty-message {
          color: var(--wh-header-title);
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 10px;
        }

        .empty-submessage {
          color: var(--wh-muted);
          font-size: 14px;
          margin: 0;
        }

        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }

        .modal-content {
          background: var(--wh-modal-bg);
          color: var(--wh-text-main);
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 10px 25px rgba(0,0,0,0.5);
          scrollbar-width: none;
          -ms-overflow-style: none;
          border: 1px solid var(--wh-modal-border);
        }

        .modal-content::-webkit-scrollbar {
          display: none;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid var(--wh-card-border);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          margin: 0;
          font-weight: 600;
          color: var(--wh-header-title);
          font-size: 1.25rem;
        }

        .btn-close {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--wh-muted);
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          padding: 20px;
          border-top: 1px solid var(--wh-card-border);
          text-align: right;
        }

        .detail-section {
          margin: 20px 0;
          padding: 15px;
          background: var(--wh-detail-section-bg);
          border-radius: 8px;
          border: 1px solid var(--wh-detail-section-border);
        }

        .detail-section h6 {
          margin-bottom: 15px;
          color: var(--wh-header-title);
          font-weight: 600;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--wh-detail-section-border);
        }

        .detail-label {
          font-weight: 600;
          color: var(--wh-muted);
          font-size: 14px;
        }

        .detail-value {
          color: var(--wh-text-main);
          font-size: 14px;
          text-align: right;
          max-width: 60%;
        }

        .transaction-id {
          font-family: monospace;
          font-size: 12px;
          word-break: break-all;
        }

        .wallet-address {
          word-break: break-all;
          font-family: monospace;
          font-size: 12px;
        }

        .reject-section {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #545b62;
        }

        .text-danger {
          color: #dc3545 !important;
        }

        .text-success {
          color: #198754 !important;
        }
      `}</style>
    </motion.div>
  );
};

export default WithdrawalHistory;
